
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import { useNavigate } from "react-router-dom";

interface DangerTabProps {
  isMobile: boolean;
}

export function DangerTab({ isMobile }: DangerTabProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleDeleteAccount = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');
      
      // Delete profile and related data first
      const { error: dataError } = await supabase.rpc('delete_user', {
        user_id: user.id
      });
      
      if (dataError) throw dataError;
      
      // Now delete the actual user auth account
      const { error: authError } = await supabase.auth.admin.deleteUser(user.id);
      
      // Sign out after successful deletion, regardless of auth error 
      // (as the user data is already deleted)
      await supabase.auth.signOut();
      
      // Only show error if there was an auth error but continue with navigation
      if (authError) {
        console.error("Auth account deletion error:", authError);
        // Just log the error but don't throw it since we still want to complete the process
      }
      
      // Navigate to home page
      toast.success(t('account_deleted_success') || "Your account has been deleted successfully");
      navigate("/", { replace: true });
    } catch (error: any) {
      console.error("Account deletion error:", error);
      toast.error(error.message || t('account_deletion_failed') || "Failed to delete account");
    }
  };

  const handleDeleteAllReviews = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');
      
      const { error } = await supabase.rpc('delete_user_reviews', {
        user_id: user.id
      });
      
      if (error) throw error;
      toast.success(t('reviews_deleted_success') || "All your reviews have been deleted successfully");
    } catch (error: any) {
      console.error("Review deletion error:", error);
      toast.error(error.message || t('reviews_deletion_failed') || "Failed to delete reviews");
    }
  };

  const handleDeleteAllFilms = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');
      
      const { error } = await supabase
        .from('film_inventory')
        .delete()
        .eq('user_id', user.id);
      
      if (error) throw error;
      toast.success(t('films_deleted_success') || "All your films have been deleted successfully");
    } catch (error: any) {
      console.error("Film deletion error:", error);
      toast.error(t('films_deletion_failed') || "Failed to delete films");
    }
  };

  return (
    <Card>
      <CardHeader className="text-left">
        <CardTitle>{t('data_management')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6 text-left">
        <div className="space-y-4">
          <h3 className="font-medium">{t('delete_all_films')}</h3>
          <p className="text-sm text-muted-foreground">
            {t('delete_films_warning')}
          </p>
          <Button 
            variant="destructive"
            onClick={() => {
              if (window.confirm(t('confirm_delete_films') || "Are you sure you want to delete all your films? This action cannot be undone.")) {
                handleDeleteAllFilms();
              }
            }}
            className={isMobile ? 'w-full' : ''}
          >
            {t('delete_all_films')}
          </Button>
        </div>

        <div className="pt-4 border-t space-y-4">
          <h3 className="font-medium">{t('delete_all_reviews')}</h3>
          <p className="text-sm text-muted-foreground">
            {t('delete_reviews_warning')}
          </p>
          <Button 
            variant="destructive"
            onClick={() => {
              if (window.confirm(t('confirm_delete_reviews') || "Are you sure you want to delete all your reviews? This action cannot be undone.")) {
                handleDeleteAllReviews();
              }
            }}
            className={isMobile ? 'w-full' : ''}
          >
            {t('delete_all_reviews')}
          </Button>
        </div>

        <div className="pt-4 border-t space-y-4">
          <h3 className="font-medium">{t('delete_account')}</h3>
          <p className="text-sm text-muted-foreground">
            {t('delete_account_warning')}
          </p>
          <Button 
            variant="destructive"
            onClick={() => {
              if (window.confirm(t('confirm_delete_account') || "Are you sure you want to delete your account? This action cannot be undone.")) {
                handleDeleteAccount();
              }
            }}
            className={isMobile ? 'w-full' : ''}
          >
            {t('delete_account')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
